import React, { useState } from 'react';
import './BookDemo.css';
import { Mail, Phone, MapPin, Loader2 } from 'lucide-react';
import { submitDemoRequest, DemoRequestData } from '../../../../../services/Public/DemoRequestService';
import { toast } from 'react-toastify';

const BookDemo = () => {
  const [formData, setFormData] = useState<DemoRequestData>({
    fullName: '',
    workEmail: '',
    companyName: '',
    phoneNumber: '',
    numberOfEmployees: '',
    needs: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear any previous status when user starts typing
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[BookDemo] Form submitted, formData:', formData);
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      console.log('[BookDemo] Calling submitDemoRequest API...');
      const response = await submitDemoRequest(formData);
      console.log('[BookDemo] API response:', response);

      if (response.success) {
        toast.success('Submitted successfully');
        setSubmitStatus({
          type: 'success',
          message: 'Submitted successfully'
        });
        // Reset form on success
        setFormData({
          fullName: '',
          workEmail: '',
          companyName: '',
          phoneNumber: '',
          numberOfEmployees: '',
          needs: '',
        });
      } else {
        toast.error(response.error?.message || 'Failed to submit request. Please try again.');
        setSubmitStatus({
          type: 'error',
          message: response.error?.message || 'Failed to submit request. Please try again.'
        });
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again later.');
      setSubmitStatus({
        type: 'error',
        message: 'An unexpected error occurred. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const whyChooseUs = [
    {
      text: 'Enterprise-grade platform',
      highlight: 'trusted by leading organizations worldwide',
    },
    {
      text: 'Dedicated support',
      highlight: 'with 24/7 availability for enterprise customers',
    },
    {
      text: 'Quick implementation',
      highlight: 'with average go-live time of 2-4 weeks',
    },
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'support.magnetix@multiplierskraft.com',
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91-9611058522',
    },
    {
      icon: MapPin,
      label: 'Headquarters',
      value: 'Bangalore, India',
    },
  ];

  return (
    <section className="book-demo-wrapper">
      <div className="book-demo-container">
        {/* Header Section */}
        <div className="book-demo-header">
          <span className="book-demo-badge">Get in Touch</span>
          <h2 className="book-demo-title">Book Your Personalized Demo</h2>
          <p className="book-demo-subtitle">
            See how our platform can transform learning in your organization. Our team will walk you through a customized demo based on your needs.
          </p>
        </div>

        {/* Content Section */}
        <div className="book-demo-content">
          {/* Left: Form */}
          <div className="book-demo-form-wrapper">
            <h3 className="form-title">Request a Demo</h3>
            <form onSubmit={handleSubmit} className="book-demo-form">
              {submitStatus.type && (
                <div className={`form-status ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="workEmail">Work Email *</label>
                  <input
                    type="email"
                    id="workEmail"
                    name="workEmail"
                    placeholder="john@company.com"
                    value={formData.workEmail}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="companyName">Company Name *</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    placeholder="Acme Corp"
                    value={formData.companyName}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="numberOfEmployees">Number of Employees *</label>
                <select
                  id="numberOfEmployees"
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                >
                  <option value="">Select...</option>
                  <option value="1-50">1-50</option>
                  <option value="51-200">51-200</option>
                  <option value="201-500">201-500</option>
                  <option value="501-1000">501-1000</option>
                  <option value="1000+">1000+</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label htmlFor="needs">Tell us about your needs</label>
                <textarea
                  id="needs"
                  name="needs"
                  placeholder="What are your learning management goals?"
                  rows={4}
                  value={formData.needs}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
                onClick={() => console.log('[BookDemo] Button clicked!')}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="spinner" size={18} />
                    Submitting...
                  </>
                ) : (
                  'Schedule Demo'
                )}
              </button>

              <p className="privacy-note">
                By submitting this form, you agree to our <a href="#">privacy policy</a>.
              </p>
            </form>
          </div>

          {/* Right: Why Choose Us + Contact Info */}
          <div className="book-demo-info">
            {/* Why Choose Us */}
            <div className="why-choose-us">
              <h3 className="info-title">Why Choose Us?</h3>
              <ul className="benefits-list">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="benefit-item">
                    <span className="benefit-dot"></span>
                    <span>
                      <strong>{item.text}</strong> {item.highlight}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Information */}
            <div className="contact-info-card">
              <h3 className="contact-info-title">Contact Information</h3>
              <div className="contact-info-list">
                {contactInfo.map((contact, index) => {
                  const IconComponent = contact.icon;
                  return (
                    <div key={index} className="contact-info-item">
                      <div className="contact-info-icon">
                        <IconComponent size={20} />
                      </div>
                      <div className="contact-info-content">
                        <span className="contact-label">{contact.label}</span>
                        <span className="contact-value">{contact.value}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookDemo;
